/**
 * Module Registry - Central registration and management of platform modules.
 *
 * This module provides the core functionality for:
 * - Registering modules with the platform
 * - Managing module lifecycle (enable/disable)
 * - Aggregating navigation, routes, and widgets from enabled modules
 * - Feature flag management
 *
 * @example
 * ```typescript
 * // Register a module
 * registerModule(examModuleConfig);
 *
 * // Check if module is enabled
 * if (isModuleEnabled('exam')) {
 *   // Use exam features
 * }
 *
 * // Get all navigation items from enabled modules
 * const navItems = getNavItems();
 * ```
 */

import type {
  ModuleCode,
  ModuleConfig,
  ModuleState,
  ModuleStatus,
  NavItem,
  RouteConfig,
  DashboardWidgetConfig,
  QuickActionConfig,
  ModuleApiResponse,
} from './types';

/**
 * Internal storage for registered modules.
 */
const moduleRegistry = new Map<ModuleCode, ModuleState>();

/**
 * Internal storage for feature flags.
 */
const featureFlags = new Map<string, boolean>();

/**
 * Event listeners for module state changes.
 */
type ModuleChangeListener = (modules: Map<ModuleCode, ModuleState>) => void;
const changeListeners = new Set<ModuleChangeListener>();

/**
 * Registers a module with the platform.
 * The module will be enabled based on its configuration and backend settings.
 *
 * @param config - The module configuration
 * @throws Error if a module with the same code is already registered
 *
 * @example
 * ```typescript
 * registerModule({
 *   code: 'exam',
 *   name: 'Exams',
 *   description: 'Academic examination system',
 *   version: '1.0.0',
 *   enabledByDefault: true,
 *   navItems: [...],
 *   routes: [...],
 * });
 * ```
 */
export function registerModule(config: ModuleConfig): void {
  if (moduleRegistry.has(config.code)) {
    throw new Error(`Module '${config.code}' is already registered`);
  }

  const state: ModuleState = {
    config,
    status: config.enabledByDefault ? 'enabled' : 'disabled',
  };

  moduleRegistry.set(config.code, state);
  notifyListeners();
}

/**
 * Unregisters a module from the platform.
 * Calls the module's onDestroy callback if defined.
 *
 * @param code - The module code to unregister
 * @returns True if the module was found and unregistered
 */
export async function unregisterModule(code: ModuleCode): Promise<boolean> {
  const state = moduleRegistry.get(code);
  if (!state) {
    return false;
  }

  // Call cleanup function
  if (state.config.onDestroy) {
    try {
      await state.config.onDestroy();
    } catch (error) {
      console.error(`Error during module '${code}' cleanup:`, error);
    }
  }

  moduleRegistry.delete(code);
  notifyListeners();
  return true;
}

/**
 * Gets the state of a registered module.
 *
 * @param code - The module code
 * @returns The module state or undefined if not registered
 */
export function getModule(code: ModuleCode): ModuleState | undefined {
  return moduleRegistry.get(code);
}

/**
 * Gets all registered modules.
 *
 * @returns Array of all module states
 */
export function getAllModules(): ModuleState[] {
  return Array.from(moduleRegistry.values());
}

/**
 * Gets all enabled modules, sorted by their order property.
 *
 * @returns Array of enabled module states
 */
export function getEnabledModules(): ModuleState[] {
  return Array.from(moduleRegistry.values())
    .filter((state) => state.status === 'enabled')
    .sort((a, b) => (a.config.order ?? 100) - (b.config.order ?? 100));
}

/**
 * Checks if a module is enabled.
 *
 * @param code - The module code to check
 * @returns True if the module exists and is enabled
 */
export function isModuleEnabled(code: ModuleCode): boolean {
  const state = moduleRegistry.get(code);
  return state?.status === 'enabled';
}

/**
 * Enables a module.
 * Validates dependencies and calls the module's onInit callback.
 *
 * @param code - The module code to enable
 * @returns True if the module was enabled successfully
 */
export async function enableModule(code: ModuleCode): Promise<boolean> {
  const state = moduleRegistry.get(code);
  if (!state) {
    console.error(`Module '${code}' is not registered`);
    return false;
  }

  // Check dependencies
  const dependencies = state.config.dependencies ?? [];
  for (const dep of dependencies) {
    if (!isModuleEnabled(dep)) {
      console.error(
        `Cannot enable module '${code}': dependency '${dep}' is not enabled`
      );
      return false;
    }
  }

  // Update status to loading
  state.status = 'loading';
  notifyListeners();

  // Call initialization function
  if (state.config.onInit) {
    try {
      await state.config.onInit();
    } catch (error) {
      state.status = 'error';
      state.error = error instanceof Error ? error.message : 'Unknown error';
      notifyListeners();
      return false;
    }
  }

  state.status = 'enabled';
  state.loadedAt = new Date();
  state.error = undefined;
  notifyListeners();
  return true;
}

/**
 * Disables a module.
 * Checks for dependent modules and calls the module's onDestroy callback.
 *
 * @param code - The module code to disable
 * @param force - If true, disables even if other modules depend on it
 * @returns True if the module was disabled successfully
 */
export async function disableModule(
  code: ModuleCode,
  force = false
): Promise<boolean> {
  const state = moduleRegistry.get(code);
  if (!state) {
    console.error(`Module '${code}' is not registered`);
    return false;
  }

  // Check for dependents (unless forcing)
  if (!force) {
    const dependents = Array.from(moduleRegistry.values())
      .filter((s) => s.status === 'enabled')
      .filter((s) => s.config.dependencies?.includes(code));

    if (dependents.length > 0) {
      const names = dependents.map((d) => d.config.code).join(', ');
      console.error(
        `Cannot disable module '${code}': other modules depend on it (${names})`
      );
      return false;
    }
  }

  // Call cleanup function
  if (state.config.onDestroy) {
    try {
      await state.config.onDestroy();
    } catch (error) {
      console.error(`Error during module '${code}' cleanup:`, error);
    }
  }

  state.status = 'disabled';
  state.loadedAt = undefined;
  notifyListeners();
  return true;
}

/**
 * Updates module states based on backend configuration.
 *
 * @param apiResponse - Array of module configurations from the API
 */
export async function syncWithBackend(
  apiResponse: ModuleApiResponse[]
): Promise<void> {
  for (const moduleData of apiResponse) {
    const state = moduleRegistry.get(moduleData.code as ModuleCode);
    if (state) {
      if (moduleData.enabled && state.status !== 'enabled') {
        await enableModule(moduleData.code as ModuleCode);
      } else if (!moduleData.enabled && state.status === 'enabled') {
        await disableModule(moduleData.code as ModuleCode, true);
      }
    }
  }
}

/**
 * Gets all navigation items from enabled modules.
 * Items are sorted by their order property.
 *
 * @returns Sorted array of navigation items
 */
export function getNavItems(): NavItem[] {
  const items: NavItem[] = [];

  for (const state of getEnabledModules()) {
    items.push(...state.config.navItems);
  }

  return items.sort((a, b) => a.order - b.order);
}

/**
 * Gets all routes from enabled modules.
 *
 * @returns Array of route configurations
 */
export function getRoutes(): RouteConfig[] {
  const routes: RouteConfig[] = [];

  for (const state of getEnabledModules()) {
    routes.push(...state.config.routes);
  }

  return routes;
}

/**
 * Gets all dashboard widgets from enabled modules.
 * Widgets are sorted by their order property.
 *
 * @returns Sorted array of widget configurations
 */
export function getDashboardWidgets(): DashboardWidgetConfig[] {
  const widgets: DashboardWidgetConfig[] = [];

  for (const state of getEnabledModules()) {
    if (state.config.dashboardWidgets) {
      widgets.push(...state.config.dashboardWidgets);
    }
  }

  return widgets.sort((a, b) => a.order - b.order);
}

/**
 * Gets all quick actions from enabled modules.
 * Actions are sorted by their order property.
 *
 * @returns Sorted array of quick action configurations
 */
export function getQuickActions(): QuickActionConfig[] {
  const actions: QuickActionConfig[] = [];

  for (const state of getEnabledModules()) {
    if (state.config.quickActions) {
      actions.push(...state.config.quickActions);
    }
  }

  return actions.sort((a, b) => a.order - b.order);
}

// =============================================================================
// Feature Flags
// =============================================================================

/**
 * Sets a feature flag value.
 *
 * @param flag - The feature flag name
 * @param enabled - Whether the feature is enabled
 */
export function setFeatureFlag(flag: string, enabled: boolean): void {
  featureFlags.set(flag, enabled);
}

/**
 * Sets multiple feature flags at once.
 *
 * @param flags - Object mapping flag names to their enabled states
 */
export function setFeatureFlags(flags: Record<string, boolean>): void {
  for (const [flag, enabled] of Object.entries(flags)) {
    featureFlags.set(flag, enabled);
  }
}

/**
 * Checks if a feature flag is enabled.
 * Also checks module-specific feature flags.
 *
 * @param flag - The feature flag name
 * @returns True if the feature is enabled
 */
export function isFeatureEnabled(flag: string): boolean {
  // Check global feature flags
  if (featureFlags.has(flag)) {
    return featureFlags.get(flag) === true;
  }

  // Check module-specific feature flags
  for (const state of getEnabledModules()) {
    if (state.config.featureFlags?.[flag] !== undefined) {
      return state.config.featureFlags[flag];
    }
  }

  return false;
}

/**
 * Gets all feature flags and their values.
 *
 * @returns Object mapping flag names to their enabled states
 */
export function getAllFeatureFlags(): Record<string, boolean> {
  const flags: Record<string, boolean> = {};

  // Add global flags
  featureFlags.forEach((value, key) => {
    flags[key] = value;
  });

  // Add module-specific flags (don't override global)
  for (const state of getEnabledModules()) {
    if (state.config.featureFlags) {
      for (const [key, value] of Object.entries(state.config.featureFlags)) {
        if (flags[key] === undefined) {
          flags[key] = value;
        }
      }
    }
  }

  return flags;
}

// =============================================================================
// Event Listeners
// =============================================================================

/**
 * Subscribes to module state changes.
 *
 * @param listener - Callback function called when module states change
 * @returns Unsubscribe function
 */
export function subscribeToChanges(listener: ModuleChangeListener): () => void {
  changeListeners.add(listener);
  return () => {
    changeListeners.delete(listener);
  };
}

/**
 * Notifies all listeners of state changes.
 */
function notifyListeners(): void {
  const snapshot = new Map(moduleRegistry);
  for (const listener of changeListeners) {
    try {
      listener(snapshot);
    } catch (error) {
      console.error('Error in module change listener:', error);
    }
  }
}

// =============================================================================
// Utility Functions
// =============================================================================

/**
 * Clears all registered modules and feature flags.
 * Primarily for testing purposes.
 */
export function clearRegistry(): void {
  moduleRegistry.clear();
  featureFlags.clear();
  notifyListeners();
}

/**
 * Gets a summary of the current module registry state.
 * Useful for debugging.
 *
 * @returns Object with registry summary
 */
export function getRegistrySummary(): {
  totalModules: number;
  enabledModules: number;
  disabledModules: number;
  loadingModules: number;
  errorModules: number;
  modules: Array<{ code: string; status: ModuleStatus; version: string }>;
} {
  const modules = Array.from(moduleRegistry.values());

  return {
    totalModules: modules.length,
    enabledModules: modules.filter((m) => m.status === 'enabled').length,
    disabledModules: modules.filter((m) => m.status === 'disabled').length,
    loadingModules: modules.filter((m) => m.status === 'loading').length,
    errorModules: modules.filter((m) => m.status === 'error').length,
    modules: modules.map((m) => ({
      code: m.config.code,
      status: m.status,
      version: m.config.version,
    })),
  };
}
